// Sentry configuration - only initialized in production
import * as Sentry from '@sentry/react';

export const initSentry = () => {
  // Only initialize Sentry in production
  if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,

      // Setting this option to true will send default PII data to Sentry.
      // For example, automatic IP address collection on events
      sendDefaultPii: false, // Set to false for privacy, only enable if needed

      // Set release version from package.json or environment
      release: import.meta.env.VITE_SENTRY_RELEASE || `admin-dashboard@${import.meta.env.VITE_APP_VERSION || '1.0.0'}`,

      // Set environment
      environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'production',

      // Configure sample rate for performance monitoring (0.0 = 0%, 1.0 = 100%)
      tracesSampleRate: 0.0, // Disabled - only track critical errors

      // Configure sample rate for error reporting
      sampleRate: 1.0, // 100% of critical errors

      // Ignore specific errors
      ignoreErrors: [
        // Network errors that are expected in production
        /NetworkError/i,
        /Failed to fetch/i,
        /AbortError/i,
        // Chunk loading errors (common after deployments)
        /Loading chunk \d+ failed/i,
        // React errors that are handled gracefully
        /Minified React error/i,
        // Browser extensions errors
        /Non-Error promise rejection captured/i,
      ],

      // Ignore specific URLs
      denyUrls: [
        // Browser extensions
        /extensions\//i,
        /^chrome:\/\//i,
        /^chrome-extension:\/\//i,
        /^moz-extension:\/\//i,
        /^safari-extension:\/\//i,
        // Third-party scripts that we don't control
        /googletagmanager\.com/i,
        /google-analytics\.com/i,
        /doubleclick\.net/i,
        /facebook\.com/i,
        /connect\.facebook\.net/i,
      ],

      // Configure integrations - using new API
      integrations: [
        // Browser profiling - using new API
        Sentry.browserTracingIntegration(),
      ],

      // Before sending events, add extra context or filter
      beforeSend(event) {
        // Add user context if available
        if (event.user === undefined) {
          const userStr = localStorage.getItem('dhamaka_user');
          if (userStr) {
            try {
              const user = JSON.parse(userStr);
              event.user = {
                id: user.id,
                email: user.email,
                username: user.username,
              };
            } catch (e) {
              // Ignore parsing errors
            }
          }
        }

        // Filter out sensitive data
        if (event.exception) {
          // Check for sensitive data in error messages
          const sensitivePatterns = [
            /password/i,
            /token/i,
            /secret/i,
            /key/i,
            /auth/i,
          ];

          const errorMessage = event.exception.values?.[0]?.value || '';
          if (sensitivePatterns.some(pattern => pattern.test(errorMessage))) {
            // Don't send errors that might contain sensitive data
            return null;
          }
        }

        return event;
      },

      // Set initial tags
      initialScope: {
        tags: {
          app: 'admin-dashboard',
          framework: 'react',
          buildTool: 'vite',
        },
      },
    });

    console.log('Sentry initialized in production mode');
  } else {
    console.log('Sentry disabled in development mode');
  }
};

// Export Sentry instance for manual usage
export { Sentry };

// Helper functions for custom error reporting
export const reportError = (error: Error, context?: Record<string, any>) => {
  if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
    Sentry.withScope((scope) => {
      if (context) {
        Object.entries(context).forEach(([key, value]) => {
          scope.setContext(key, value);
        });
      }
      Sentry.captureException(error);
    });
  }
};

export const reportMessage = (message: string, level: Sentry.SeverityLevel = 'info', context?: Record<string, any>) => {
  if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
    Sentry.withScope((scope) => {
      if (context) {
        Object.entries(context).forEach(([key, value]) => {
          scope.setContext(key, value);
        });
      }
      Sentry.captureMessage(message, level);
    });
  }
};

// Helper to set user context
export const setUserContext = (user: { id: string; email?: string; username?: string } | null) => {
  if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
    if (user) {
      Sentry.setUser(user);
    } else {
      Sentry.setUser(null);
    }
  }
};